import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CellContext } from "@tanstack/react-table";
import { HiOutlineUser, HiOutlineMail, HiOutlinePhone, HiOutlineClock } from "react-icons/hi";

import { CourseFormFieldApi } from "api";
import { CourseStudentApi, CourseStudentTrancheApi } from "api/courseStudentsApi";
import { useCourseStudents } from "api/hooks";
import { courseRegistrationApi, RetainedRevenueChoice } from "api/registrations";
import { CourseTrancheApi } from "api/types";
import { TanTableClient, UserAvatar } from "components/molecules";
import { RetainedRevenueModal } from "components/templates/RetainedRevenue/RetainedRevenueModal";
import { PaziaButton } from "components/atoms";
import { REGISTRATION_TRANCHE_PAYMENT_STATUSES_LABELS, RegistrationTrancheStatus, routes } from "consts";
import { formatDate, toMoneyFormat, isParseableDate, useToaster } from "utils";
import { ColumnsDrawer } from "./ColumnsDrawer";
import { buildFieldGroups, getDefaultColumnOrder } from "./columnGroups";
import { FieldSet, getOrderedFields, loadSavedSets } from "./savedViews";
import { loadTableState, PaidFilter, saveTableState } from "./tableState";
import {
  ColumnMeta,
  customMapStudents,
  getColumnMeta,
  StudentColumnDef,
  TrancheStatusClasses,
} from "../studentColumns";

interface StudentsTableViewProps {
  courseId: number;
  exportFileName: string;
  fields: CourseFormFieldApi[];
  tranches: CourseTrancheApi[];
}

export const StudentsTableView = ({ courseId, exportFileName, fields, tranches }: StudentsTableViewProps) => {
  const { students, total, invalidate } = useCourseStudents(courseId);
  const navigate = useNavigate();
  const { showSuccess, showError } = useToaster();

  const groups = useMemo(() => buildFieldGroups(fields, tranches), [fields, tranches]);
  const [selectedFields, setSelectedFields] = useState<string[]>(() => {
    const validKeys = new Set(groups.flatMap((g) => g.fields).map((f) => f.key));
    const restored = loadTableState(courseId)?.selectedFields.filter((key) => validKeys.has(key));
    return restored && restored.length > 0 ? restored : getDefaultColumnOrder(groups, tranches);
  });

  const [search, setSearch] = useState(() => loadTableState(courseId)?.search ?? "");
  const [paidFilter, setPaidFilter] = useState<PaidFilter>(() => loadTableState(courseId)?.paidFilter ?? "all");
  const [selectedStudents, setSelectedStudents] = useState<number[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [savedSets, setSavedSets] = useState<FieldSet[]>(() => loadSavedSets());
  const [selectedSetName, setSelectedSetName] = useState(() => {
    const restored = loadTableState(courseId)?.selectedSetName;
    if (!restored) return "";
    return loadSavedSets().some((s) => s.name === restored) ? restored : "";
  });

  useEffect(() => {
    saveTableState(courseId, { selectedFields, search, paidFilter, selectedSetName });
  }, [courseId, selectedFields, search, paidFilter, selectedSetName]);

  const toggleField = (key: string) =>
    setSelectedFields((current) => (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]));
  const toggleGroup = (groupFields: { key: string }[], value: boolean) =>
    setSelectedFields((current) => {
      if (value) {
        const missing = groupFields.map((f) => f.key).filter((k) => !current.includes(k));
        return [...current, ...missing];
      }
      const groupKeys = new Set(groupFields.map((f) => f.key));
      return current.filter((k) => !groupKeys.has(k));
    });
  const resetVisibility = () => setSelectedFields(getDefaultColumnOrder(groups, tranches));
  const loadFieldSet = (set: FieldSet) => setSelectedFields(getOrderedFields(set, groups));

  const columnMetas = useMemo(() => {
    return selectedFields
      .map((key) => getColumnMeta(key, fields, tranches))
      .filter((m): m is ColumnMeta => m !== null);
  }, [selectedFields, fields, tranches]);

  const columns = useMemo(
    () => [...buildColumns(columnMetas, navigate, courseId), buildActionsColumn(navigate, courseId)],
    [columnMetas, navigate, courseId],
  );

  const data = useMemo(() => customMapStudents(students, total, tranches).data, [students, total, tranches]);

  const filteredData = useMemo(() => {
    let list = data;
    if (paidFilter === "paid") list = list.filter((s: any) => s.fullyPaid);
    if (paidFilter === "unpaid") list = list.filter((s: any) => !s.fullyPaid);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((s: any) => (s.fullName as string).toLowerCase().includes(q));
    return list;
  }, [data, paidFilter, search]);

  const handleSendEmail = () => {
    const recipientIds = selectedStudents.join(",");
    navigate(`${routes.EMAILS}${routes.SCHEDULED_EMAILS_CREATE}?recipients=${recipientIds}`);
  };

  const handleSendSMS = () => {
    const recipientIds = selectedStudents.join(",");
    navigate(`${routes.EMAILS}${routes.SCHEDULED_MESSAGES_CREATE}?recipients=${recipientIds}`);
  };

  const handleDeleteSelected = async (retainedRevenue: RetainedRevenueChoice) => {
    if (isDeleting) return;
    setIsDeleting(true);
    const results = await Promise.allSettled(
      selectedStudents.map(async (userId) => {
        const registrationId = await courseRegistrationApi.getRegistrationId(courseId, userId);
        await courseRegistrationApi.delete(courseId, registrationId, retainedRevenue);
      }),
    );
    const failedCount = results.filter((r) => r.status === "rejected").length;
    const succeededCount = results.length - failedCount;

    if (failedCount === 0) {
      showSuccess(`Usunięto ${succeededCount} kursantów z kursu.`);
    } else if (succeededCount === 0) {
      showError(`Nie udało się usunąć kursantów z kursu.`);
    } else {
      showError(`Usunięto ${succeededCount} z ${results.length} kursantów. ${failedCount} nie powiodło się.`);
    }

    setIsDeleting(false);
    setDeleteModalOpen(false);
    setSelectedStudents([]);
    invalidate();
  };

  const chipClass = (active: boolean) =>
    `rounded-md px-3.5 py-1.5 text-sm font-semibold ${
      active ? "bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-100" : "text-slate-500 dark:text-slate-400"
    }`;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Szukaj po nazwisku..."
          className="form-input w-60 bg-white dark:bg-slate-800"
        />

        <div className="inline-flex gap-0.5 rounded-lg border border-slate-200 bg-slate-100 p-0.5 dark:border-slate-700 dark:bg-slate-900">
          <button onClick={() => setPaidFilter("all")} className={chipClass(paidFilter === "all")}>
            Wszyscy
          </button>
          <button onClick={() => setPaidFilter("paid")} className={chipClass(paidFilter === "paid")}>
            Opłaceni
          </button>
          <button onClick={() => setPaidFilter("unpaid")} className={chipClass(paidFilter === "unpaid")}>
            Nieopłaceni
          </button>
        </div>

        <select
          value={selectedSetName}
          onChange={(e) => {
            setSelectedSetName(e.target.value);
            const set = savedSets.find((s) => s.name === e.target.value);
            if (set) loadFieldSet(set);
          }}
          disabled={savedSets.length === 0}
          className="form-input w-52 bg-white text-sm dark:bg-slate-800"
        >
          <option value="">
            {savedSets.length === 0 ? "Brak zapisanych widoków" : "Zapisane widoki…"}
          </option>
          {savedSets.map((set) => (
            <option key={set.name} value={set.name}>
              {set.name}
            </option>
          ))}
        </select>

        <div className="flex-1" />

        <PaziaButton type="tertiary" btnType="button" onClick={() => setDrawerOpen(true)}>
          <span className="inline-flex items-center gap-2">
            Kolumny
            <span className="rounded bg-primary-100 px-1.5 py-0.5 text-xs font-bold text-primary-600 dark:bg-primary-900 dark:text-primary-300">
              {columnMetas.length}
            </span>
          </span>
        </PaziaButton>
      </div>

      {selectedStudents.length > 0 && (
        <div className="mb-3 flex items-center gap-3.5 rounded-xl bg-primary-500 px-4 py-2.5 text-sm text-white">
          <span className="font-bold">{selectedStudents.length} zaznaczonych</span>
          <div className="h-5 w-px bg-white/30" />
          <button onClick={handleSendEmail} className="rounded px-2 py-1 font-semibold hover:bg-white/15">
            Wyślij email
          </button>
          <button onClick={handleSendSMS} className="rounded px-2 py-1 font-semibold hover:bg-white/15">
            Wyślij SMS
          </button>
          <button onClick={() => setDeleteModalOpen(true)} className="rounded px-2 py-1 font-semibold hover:bg-white/15">
            Usuń z kursu
          </button>
          <div className="flex-1" />
          <button onClick={() => setSelectedStudents([])} className="font-semibold opacity-85 hover:opacity-100">
            Odznacz wszystko
          </button>
        </div>
      )}

      <TanTableClient
        exportFileName={exportFileName}
        key={columnMetas.map((c) => c.key).join(",")}
        data={filteredData}
        columns={columns}
        pageSize={50}
        selectOnClick
        onSelectionChange={setSelectedStudents}
        exportCsv
      />

      <RetainedRevenueModal
        id="delete-selected-students"
        isOpen={deleteModalOpen}
        title="Usuń kursantów z kursu"
        description={`Czy na pewno chcesz usunąć ${selectedStudents.length} ${
          selectedStudents.length === 1 ? "kursanta" : "kursantów"
        } z kursu? Tej operacji nie można cofnąć. Wybrana opcja przychodu dotyczy każdego kursanta — kwoty można później zmienić w zestawieniu finansowym kursu.`}
        onCloseRequest={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteSelected}
        confirmText={isDeleting ? "Usuwanie…" : "Tak, usuń"}
      />

      <ColumnsDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        groups={groups}
        visibleColCount={columnMetas.length}
        selectedFields={selectedFields}
        onToggleField={toggleField}
        onToggleGroup={toggleGroup}
        onReset={resetVisibility}
        onLoadFieldSet={loadFieldSet}
        onSavedSetsChange={(sets) => {
          setSavedSets(sets);
          if (!sets.some((s) => s.name === selectedSetName)) setSelectedSetName("");
        }}
        onFieldSetSaved={(name) => setSelectedSetName(name)}
      />
    </div>
  );
};

// Plain values (lp, id, email, phone, notes text...) are shown as-is — no date guessing, since
// small numbers like the "Lp" index are also technically valid Date() timestamps (epoch + ms).
const displayValue = (value: unknown): React.ReactNode => {
  if (value === undefined || value === null || value === "") return <span className="text-slate-400">—</span>;
  return value as React.ReactNode;
};

// Only custom registration-form field values are untyped strings coming from the API where a
// date guess makes sense (mirrors the old FieldsSelection.tsx fallback column behavior).
const customFieldValue = (value: unknown): React.ReactNode => {
  if (value === undefined || value === null || value === "") return <span className="text-slate-400">—</span>;
  if (isParseableDate(value)) return formatDate(new Date(value as string), false, true);
  return value as React.ReactNode;
};

const dateValue = (value: unknown, withTime: boolean): React.ReactNode => {
  if (!value) return <span className="text-slate-400">—</span>;
  const date = new Date(value as string | Date);
  return date.toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
};

const buildActionsColumn = (navigate: ReturnType<typeof useNavigate>, courseId: number): StudentColumnDef => ({
  id: "actions",
  accessorKey: "id",
  header: "Akcje",
  cell: (info: CellContext<CourseStudentApi, unknown>) => {
    const row = info.row.original as CourseStudentApi;
    const goToStudent = () => navigate(routes.STUDENT_NESTED.COURSES(row.id, courseId));
    const goToEmail = () => navigate(`${routes.EMAILS}${routes.SCHEDULED_EMAILS_CREATE}?recipients=${row.id}`);
    const goToSMS = () => navigate(`${routes.EMAILS}${routes.SCHEDULED_MESSAGES_CREATE}?recipients=${row.id}`);
    return (
      <div onClick={(e) => e.stopPropagation()} className="flex items-center justify-end gap-1.5">
        {row.cancellationRequestedAt && (
          <span title="Zgłoszona rezygnacja" className="flex h-9 w-9 items-center justify-center text-amber-500">
            <HiOutlineClock className="h-5 w-5" />
          </span>
        )}
        <button
          title="Profil"
          onClick={goToStudent}
          className="flex h-9 w-9 items-center justify-center rounded text-slate-400 hover:bg-primary-50 hover:text-primary-600"
        >
          <HiOutlineUser className="h-5 w-5" />
        </button>
        <button
          title="Wyślij email"
          onClick={goToEmail}
          className="flex h-9 w-9 items-center justify-center rounded text-slate-400 hover:bg-primary-50 hover:text-primary-600"
        >
          <HiOutlineMail className="h-5 w-5" />
        </button>
        <button
          title="Wyślij SMS"
          onClick={goToSMS}
          className="flex h-9 w-9 items-center justify-center rounded text-slate-400 hover:bg-primary-50 hover:text-primary-600"
        >
          <HiOutlinePhone className="h-5 w-5" />
        </button>
      </div>
    );
  },
});

const buildColumns = (
  metas: ColumnMeta[],
  navigate: ReturnType<typeof useNavigate>,
  courseId: number,
): StudentColumnDef[] =>
  metas.map((meta) => ({
    id: meta.key,
    accessorKey: meta.key,
    header: meta.label,
    getExportValue:
      meta.kind === "tranche"
        ? (row: any) => {
            const tranche = row[meta.key] as CourseStudentTrancheApi | undefined;
            if (!tranche) return "";
            return `${toMoneyFormat(tranche.discountedAmount, true)} - ${REGISTRATION_TRANCHE_PAYMENT_STATUSES_LABELS[tranche.status as RegistrationTrancheStatus]}`;
          }
        : meta.kind === "additionalTranches"
          ? (row: any) => {
              const list = (row.additionalTranches as CourseStudentTrancheApi[]) ?? [];
              return list
                .map(
                  (t) =>
                    `${toMoneyFormat(t.discountedAmount, true)} - ${REGISTRATION_TRANCHE_PAYMENT_STATUSES_LABELS[t.status as RegistrationTrancheStatus]}`,
                )
                .join("; ");
            }
          : undefined,
    cell: (info: CellContext<CourseStudentApi, unknown>) => {
      const row = info.row.original as any;
      switch (meta.kind) {
        case "name":
          return (
            <div className="flex items-center gap-2.5">
              <UserAvatar avatar={row.avatar ?? undefined} firstName={row.firstName} surname={row.surname} alt={row.fullName} />
              <div>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(routes.STUDENT_NESTED.COURSES(row.id, courseId));
                  }}
                  className="cursor-pointer font-semibold text-slate-900 hover:underline dark:text-slate-100"
                >
                  {row.fullName}
                </div>
                <div className="text-xs text-slate-400">{row.email}</div>
              </div>
            </div>
          );
        case "paid":
          return row.fullyPaid ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Opłacone
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">
              <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
              Nieopłacone
            </span>
          );
        case "boolean":
          return row[meta.key] ? (
            <span className="font-semibold text-emerald-600">TAK</span>
          ) : (
            <span className="font-semibold text-rose-600">NIE</span>
          );
        case "tranche": {
          const tranche = row[meta.key] as CourseStudentTrancheApi | undefined;
          if (!tranche) return displayValue(null);
          return (
            <span className={TrancheStatusClasses[tranche.status as RegistrationTrancheStatus]}>
              {toMoneyFormat(tranche.discountedAmount, true)} •{" "}
              {REGISTRATION_TRANCHE_PAYMENT_STATUSES_LABELS[tranche.status as RegistrationTrancheStatus]}
            </span>
          );
        }
        case "additionalTranches": {
          const list = (row.additionalTranches as CourseStudentTrancheApi[]) ?? [];
          if (!list || list.length === 0) return displayValue(null);
          return (
            <div className="space-y-0.5">
              {list.map((t, idx) => (
                <div key={idx} className={TrancheStatusClasses[t.status as RegistrationTrancheStatus]}>
                  {toMoneyFormat(t.discountedAmount, true)} •{" "}
                  {REGISTRATION_TRANCHE_PAYMENT_STATUSES_LABELS[t.status as RegistrationTrancheStatus]}
                </div>
              ))}
            </div>
          );
        }
        case "notes":
          return displayValue(row[meta.key]);
        case "date":
          return dateValue(row[meta.key], false);
        case "date-with-time":
          return dateValue(row[meta.key], true);
        case "customField":
          return customFieldValue(row[meta.key]);
        case "plain":
        default:
          return displayValue(row[meta.key]);
      }
    },
  }));
